// eslint-disable-next-line no-restricted-imports
import type {QueryClient} from '@tanstack/react-query';

export const extractSerializableData = (
  queryClient: QueryClient,
): Array<[string, {data?: Record<string, unknown>}]> => {
  return queryClient
    .getQueryCache()
    .getAll()
    .map(query => [
      query.queryKey[0] as string,
      {data: query.state.data as Record<string, unknown>},
    ]);
};

export const serializeJS = (
  value: ReturnType<typeof extractSerializableData>,
): string => {
  const patchedValue = value.map(([key, queryData]) => {
    if (!queryData || !queryData.data) {
      return [key, queryData];
    }

    const patchedData = Object.entries(queryData.data).reduce((acc, [k, v]) => {
      if (isUint8Array(v)) {
        acc[k] = patchUint8Array(v);
      } else {
        acc[k] = v;
      }
      return acc;
    }, {} as Record<string, unknown>);
    return [key, {data: patchedData}];
  });
  return JSON.stringify(patchedValue).replace(/</g, '\\u003c');
};

export const deserializeJS = (
  value: string,
): ReturnType<typeof extractSerializableData> => {
  const parsedValue = JSON.parse(value);
  return parsedValue.map(
    ([key, queryData]: [string, {data?: Record<string, unknown>}]) => {
      if (!queryData || !queryData.data) {
        return [key, queryData];
      }
      const unpatchedData = Object.entries(queryData.data).reduce(
        (acc, [k, v]) => {
          if (isPatchedUint8Array(v)) {
            acc[k] = unpatchUint8Array(v);
          } else {
            acc[k] = v;
          }
          return acc;
        },
        {} as Record<string, unknown>,
      );
      return [key, {data: unpatchedData}];
    },
  );
};

const IDENTIFIER_Uint8Array = '_Uint8Array_';

const isUint8Array = (value: unknown): value is Uint8Array => {
  return value instanceof Uint8Array;
};

const patchUint8Array = (value: Uint8Array) => {
  return {
    __type: IDENTIFIER_Uint8Array,
    data: Array.from(value),
  };
};

const isPatchedUint8Array = (
  value: unknown,
): value is {__type: typeof IDENTIFIER_Uint8Array; data: number[]} => {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as any).__type === IDENTIFIER_Uint8Array
  );
};

const unpatchUint8Array = (value: {
  __type: typeof IDENTIFIER_Uint8Array;
  data: number[];
}): Uint8Array => {
  return new Uint8Array(value.data);
};
